import {useLogin, useUserInfo} from '@/services/global.store';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import {toLogin} from '../../me.variable';
import {goTo, toAgentApply} from '@/utils';
import {postUserInfo} from '@/services/global.service';
import {VipUpgradeContext} from '@/common-pages/vip/provider/vip-upgrade-provider';
import {useFocusEffect} from '@react-navigation/native';
import {getTaskCount} from '@/pages/promotions/service';
import {homePage} from '@/config';
import {useMeStore} from '../../hooks';
import {currentBrand} from '@/brands';

export const RefreshProvider = createContext<{
  refresh: (isClick?: boolean) => void;
}>({
  refresh: () => {},
});

export function useMeUser() {
  const login = useLogin();
  const user = useUserInfo();

  const {t} = useTranslation();
  const [heightPercent, setHeightPercent] = useState(0);

  useEffect(() => {
    const container = window.document.getElementById('me-container');

    const handleScroll = () => {
      const scrollY = container?.scrollTop || 0;
      if (scrollY <= 40) {
        setHeightPercent(0);
      } else if (scrollY >= 70) {
        setHeightPercent(1);
      } else {
        setHeightPercent(0.5 + (0.5 * (scrollY - 40)) / 30);
      }
    };

    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const toProfile = useCallback(() => {
    if (!login) {
      toLogin();
      return;
    }
    goTo('Profile');
  }, [login]);

  return {login, user, heightPercent, t, toProfile};
}

export function useMeHeader() {
  const login = useLogin();
  const user = useUserInfo();
  const {setLoading} = useMeStore();

  const vipCardRef = useRef<{init: () => void}>(null);
  const updateVipCard = useCallback(() => {
    vipCardRef.current?.init();
  }, []);

  const refresh = useCallback(
    async (isClick: boolean = false) => {
      try {
        updateVipCard();
        console.log('isClick', isClick);
        isClick && setLoading(true);
        if (login) {
          await postUserInfo();
        }
      } catch (e) {
        console.log('e', e);
      } finally {
        setLoading(false);
      }
    },
    [updateVipCard, login, setLoading],
  );

  const vipUpgradeProvider = useContext(VipUpgradeContext);

  useEffect(() => {
    vipUpgradeProvider.showVipUpgrade(() => {
      updateVipCard();
    });
  }, [vipUpgradeProvider, updateVipCard]);

  return {login, user, refresh, vipCardRef};
}

export function useGoTo() {
  const {t} = useTranslation();
  const login = useLogin();
  const user = useUserInfo();

  const toRoute = useCallback(
    (
      name: string,
      params: Record<string, any> = {},
      needLogin: boolean = true,
    ) => {
      if (needLogin && !login) {
        toLogin();
        return;
      }
      goTo(name, params);
    },
    [login],
  );

  const toRecharge = useCallback(() => {
    toRoute('Recharge');
  }, [toRoute]);

  const toWithdraw = useCallback(() => {
    toRoute('Withdraw');
  }, [toRoute]);

  const toRechargeRecords = useCallback(() => {
    toRoute('RechargeRecords');
  }, [toRoute]);

  const toWithdrawRecords = useCallback(() => {
    toRoute('WithdrawRecords');
  }, [toRoute]);

  const toBet = useCallback(() => {
    toRoute('Bets');
  }, [toRoute]);

  const toProxy = useCallback(() => {
    if (!login) {
      toAgentApply();
      return;
    }
    if (user?.isAgent === 1) {
      goTo('ProxyHome');
    } else {
      toAgentApply();
    }
  }, [login, user?.isAgent]);

  const toCommission = useCallback(() => {
    toRoute(homePage, {
      screen: currentBrand.key === 'anna' ? 'Referral' : 'Invitation',
    });
  }, [toRoute]);

  const toTransactions = useCallback(() => {
    toRoute('Transactions');
  }, [toRoute]);

  const toPassword = useCallback(() => {
    toRoute('SetPassword');
  }, [toRoute]);

  return {
    t,
    login,
    user,
    toRecharge,
    toWithdraw,
    toRechargeRecords,
    toWithdrawRecords,
    toRoute,
    toBet,
    toProxy,
    toCommission,
    toTransactions,
    toPassword,
  };
}

export function useMePromotions() {
  const {t} = useTranslation();
  const [taskCount, setTaskCount] = useState(0);
  const login = useLogin();

  useFocusEffect(
    useCallback(() => {
      if (!login) {
        return;
      }
      getTaskCount().then(data => {
        setTaskCount(data);
      });
    }, [login]),
  );

  const mePromotionsWinImage = useMemo(() => {
    return require('@/assets/icons/me/me-promotions-win.webp');
  }, []);

  const mePromotionsBonusImage = useMemo(() => {
    return require('@/assets/icons/me/me-promotions-bonus.webp');
  }, []);

  return {
    taskCount,
    mePromotionsWinImage,
    mePromotionsBonusImage,
    t,
    login,
  };
}
